import React, { useEffect, useState } from "react";
import "../../App.css";
import LinkDrop from "./link_drop";
import SudokuIcon from "../Assets/sudoku.png";
import CombinationIcon from "../Assets/combination.png";
import NonoIcon from "../Assets/nonogram.png";
import SolitareIcon from "../Assets/solitare.png";
import SudoblockIcon from "../Assets/sudoblock.png";
import SudotetroidIcon from "../Assets/sudotetroid.png";
import { House, User, Settings } from "lucide-react";
import WordleIcon from "../Assets/wordle_logo.png";
import MinequestIcon from "../Assets/minequest_logo.png";
import SolitaireIcon from "../Assets/solitaire_logo.png";
import SpellingBeeIcon from "../Assets/spelling_bee_logo.png";
import WordSearchIcon from "../Assets/word_search_logo.png";
import ConnectionsIcon from "../Assets/connections_logo.png";

import { useMediaQuery } from "@mui/material";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import BottomNavBar from "../../pages/MobileView/BottomNavBar";
import { post } from "../../lib/api";
import { get } from "../../lib/api";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarSeparator, SidebarFooter, useSidebar } from "../ui/sidebar";
import ArchivedLinkDrop from "./link_drop_archive";
import { Link } from "react-router-dom";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup } from "../ui/select";
import { Textarea } from "../ui/textarea";

const pageIcons = {
  Settings: <Settings />,
  User: <User />,
  Combinations: CombinationIcon,
  Nonogram: NonoIcon,
  Solitare: SolitareIcon,
  Sudoku: SudokuIcon,
  Sudoblock: SudoblockIcon,
  Sudotetroid: SudotetroidIcon,
  "Rode Draad": ConnectionsIcon,
  Wordle: WordleIcon,
  Minequest: MinequestIcon,
  Solitaire: SolitaireIcon,
  "Spelling Bee": SpellingBeeIcon,
  "Word Search": WordSearchIcon,
};

const Sidebarnav = () => {
  // Destructure values and setters from SidebarContext
  const { setBrand, setAccess, setDates, setAuthenticated, authenticated, structuredPages, setComponents, setPages, setUser, setUserChildren, setGames, pages, components, setStructuredPages, user } =
    useSidebar();

  // Get the current date formatted as YYYY-MM-DD
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // padStart ensures the month is always 2 digits
  const day = String(currentDate.getDate()).padStart(2, "0"); // padStart ensures the day is always 2 digits
  const formattedDate = `${year}-${month}-${day}`;
  const isMobile = useMediaQuery(`(max-width:768px)`);
  const [activeMenu, setActiveMenu] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const [showTextArea, setShowTextArea] = useState(false);
  const [thankYouOpen, setThankYouOpen] = useState(false);
  const [selectKey, setSelectKey] = useState(0);
  const [archivedGames, setArchivedGames] = useState([]);

  // Check user authentication status on component mount
  useEffect(() => {
    const fetchAuthStatus = async () => {
      try {
        const data = await get("/session");

        if (data["isValid"]) {
          setAuthenticated(data["isValid"]);
          setUser(data["user"]);
        }
      } catch (error) {
        console.error("Error:", error);
        setAuthenticated(false);
      }
    };

    fetchAuthStatus();
  }, [setAuthenticated, setUser]);

  const handleSelectChange = (value) => {
    setSelectedValue(value);
    // Show the text area when an option is selected.
    setShowTextArea(true);
  };
  const handleDialogOpenChange = (open) => {
    // Reset select and text area on close
    if (!open) {
      // setSelectKey(prev => prev + 1);
      setShowTextArea(false);
      setSelectedValue("");
    }
    setFeedbackOpen(open);
  };

  useEffect(() => {
    if (!authenticated || !user) return; // Exit if not authenticated or user is array or null

    const fetchData = async () => {
      try {
        const getData = {
          user: user,
          method: "getUserPageAccess",
        };
        const data = await post(getData);

        setBrand(data["brand"] || []);
        setAccess(data["access"] || []);
        setDates([data["start_date"], formattedDate]);
        setComponents(data["components"] || []);
        setPages(data["pages"] || []);
        setUser(data["user_info"] || null);
        setUserChildren(data["children"] || []);
        setGames(data["games"] || []);

        //Find archived games
        const archivedGames = data["games"].filter((game) => game.isArchived).map((game) => game.name);
        setArchivedGames(archivedGames);

        //console.log("raw login data: ", data);
      } catch (error) {
        console.error("Error:", error);
      }
    };

    fetchData();
  }, [authenticated]);

  // Build structuredPages object based on pages and components
  useEffect(() => {
    let structuredPages = {};
    if (Array.isArray(pages)) {
      // First, identify all main pages
      pages.forEach((page) => {
        if (page && !page.parent_id) {
          structuredPages[page.page_id] = {
            subpages: {},
            accessibleSubpages: [], // New array to track which subpages the user can access
          };
        } else {
          // If the page has a parent_id, ensure the parent exists in structuredPages
          if (page && page.parent_id && !structuredPages[page.parent_id]) {
            structuredPages[page.parent_id] = {
              subpages: {},
              accessibleSubpages: [],
            };
          }
        }
      });

      // Then process subpages and track which ones the user has access to
      pages.forEach((page) => {
        if (page && page.parent_id && structuredPages[page.parent_id]) {
          // Add subpage to the parent page's subpages
          structuredPages[page.parent_id].subpages[page.page_id] = {};
          // Track that this subpage is accessible
          structuredPages[page.parent_id].accessibleSubpages.push(page.page_id);
        }
      });

      // Add "Planning" as an accessible subpage for each main page if it exists
      Object.keys(structuredPages).forEach((pageKey) => {
        // Check if this page exists in the pages array (has page details)
        const mainPage = pages.find((p) => p && p.page_id === pageKey && !p.parent_id);
        if (mainPage) {
          // Add "Planning" as a subpage to represent the main page view
          structuredPages[pageKey].subpages["Planning"] = {};
          structuredPages[pageKey].accessibleSubpages.push("Planning");
        }
      });
    }

    // Process components and assign them to their respective pages or subpages
    if (Array.isArray(components)) {
      components.forEach((component) => {
        if (component) {
          let targetPage = structuredPages[component.page_id];
          let targetSubpage;
          if (structuredPages[component.parent_id] && structuredPages[component.parent_id].subpages) {
            targetSubpage = structuredPages[component.parent_id].subpages[component.page_id];
          }

          if (targetPage) {
            // Add component to the main page
            targetPage.containers = targetPage.containers || [];
            targetPage.containers.push({
              title: component.component_id,
              tier: component.access_tier,
            });
          } else if (targetSubpage) {
            // Add component to the subpage
            targetSubpage.containers = targetSubpage.containers || [];
            targetSubpage.containers.push({
              title: component.component_id,
              tier: component.access_tier,
            });
          }
        }
      });
    }

    // Update the structuredPages state
    setStructuredPages(structuredPages);
  }, [pages, components, setStructuredPages]);

  // Render sidebar only if user is authenticated
  return authenticated ? (
    <>
      {isMobile ? (
        <BottomNavBar />
      ) : (
        <Sidebar
          collapsible="icon"
          className="!w-auto z-[10] flex justify-center items-start space-y-5 pt-16 fixed text-sidebar-primary-foreground
                     dark:text-[var(--text-light)]"
        >
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu className={"!pl-0"}>
                  <SidebarMenuItem className="min-w-full">
                    <SidebarMenuButton asChild className="min-w-full">
                      <Link to="/" className="no-underline appearance-none flex items-center gap-2">
                        <House className="size-6 stroke-black dark:stroke-white" />
                        <span className="text-[var(--text-dark)] dark:text-[var(--text-light)] font-medium">Home</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarSeparator />
                  {authenticated &&
                    Object.keys(structuredPages).map((pageKey) => {
                      if (pageKey.toLowerCase() === "administration") return null;
                      if (archivedGames.includes(pageKey)) return null;
                      const page = structuredPages[pageKey];
                      return (
                        <LinkDrop
                          key={pageKey}
                          pageKey={pageKey}
                          Icon={pageIcons[pageKey]}
                          page={page}
                          currentPageKey={pageKey}
                          activeMenu={activeMenu}
                          setActiveMenu={setActiveMenu}
                          accessibleSubpages={page.accessibleSubpages || []}
                        />
                      );
                    })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter>
            <SidebarSeparator />
            <SidebarMenu className={"!pl-0 !m-0"}>
              <ArchivedLinkDrop pages={Object.fromEntries(Object.entries(structuredPages).filter(([key]) => archivedGames.includes(key)))} />
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>
      )}

      {feedbackOpen && (
        <Dialog open={feedbackOpen} onOpenChange={handleDialogOpenChange} className="bg-white dark:bg-dark">
          <DialogContent className="sm:max-w-[425px] dark:text-white dark:bg-dark">
            <DialogHeader>
              <DialogTitle>{thankYouOpen ? "Thank You!" : "Help Improve the Denda Console"}</DialogTitle>
            </DialogHeader>
            {thankYouOpen ? (
              <div className="py-8 text-center text-lg font-semibold">
                Thank you for your feedback
                <span role="img"> 💖 </span>!
              </div>
            ) : (
              <div className="grid gap-4 py-4 ">
                <div className="grid grid-rows-1 justify-center items-center gap-4 ">
                  <Select key={selectKey} onValueChange={handleSelectChange} value={selectedValue}>
                    <SelectTrigger className="w-auto min-w-[180px]">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>

                    <SelectContent className="absolute left-0 top-full w-auto ">
                      <SelectGroup>
                        <SelectItem onValueChange={handleSelectChange} value="Problem">
                          Report a problem with the Denda Console
                        </SelectItem>
                        <SelectItem onValueChange={handleSelectChange} value="Feature">
                          Request a feature
                        </SelectItem>
                        <SelectItem onValueChange={handleSelectChange} value="Feedback">
                          General feedback
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                {selectedValue === "Feature" && (
                  <div className={"space-y-1.5"}>
                    <Label> What feature would you like to see? </Label>
                    <Textarea className="mt-2 w-full p-2 border rounded" />
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      {/*<InfoIcon className="h-4 w-4" />*/}
                      Reports sent to Denda are 100% anonymous and only include your message, and the version of the console you're using.{" "}
                    </div>
                  </div>
                )}

                {selectedValue === "Problem" && (
                  <div className={"space-y-1.5"}>
                    <Label> Please describe the problem in as much detail as possible: </Label>
                    <Textarea className="mt-2 w-full p-2 border rounded" />
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      {/*<InfoIcon className="h-4 w-4" />*/}
                      Reports sent to Denda are 100% anonymous and only include your message, and the version of the console you're using.{" "}
                    </div>
                  </div>
                )}

                {selectedValue === "Feedback" && (
                  <div className={"space-y-1.5"}>
                    <Label> Please give us your feedback: </Label>
                    <Textarea className="mt-2 w-full p-2 border rounded" />
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      {/*<InfoIcon className="h-4 w-4" />*/}
                      Reports sent to Denda are 100% anonymous and only include your message, and the version of the console you're using.{" "}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter asChild>
              <div className="w-full flex flex-row justify-center items-center space-x-4">
                {!thankYouOpen ? (
                  <>
                    <Button
                      className={"w-50 bg-purple-800 hover:bg-purple-900 dark:text-white"}
                      type="submit"
                      onClick={() => {
                        // setFeedbackOpen(false);
                        setThankYouOpen(true);
                        setShowTextArea(false);
                        setSelectedValue("");
                      }}
                    >
                      Submit
                    </Button>

                    <Button
                      className={"w-50 bg-purple-800 hover:bg-purple-900 dark:text-white"}
                      onClick={() => {
                        setFeedbackOpen(false);
                        setShowTextArea(false);
                        setSelectedValue("");
                      }}
                    >
                      {" "}
                      Cancel{" "}
                    </Button>
                  </>
                ) : (
                  <Button
                    className={"w-50 bg-purple-800 hover:bg-purple-900 dark:text-white flex-2"}
                    onClick={() => {
                      setFeedbackOpen(false);
                      setThankYouOpen(false);
                      setShowTextArea(false);
                      setSelectedValue("");
                    }}
                  >
                    Close
                  </Button>
                )}
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  ) : null;
};

export default Sidebarnav;
